import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Mail, 
  Building2,
  Phone,
  CheckCircle,
  Loader2,
  Activity
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inquiryTypes = [
  "Request Pricing",
  "Product Information",
  "Bulk Order Inquiry",
  "Technical Support",
  "General Inquiry"
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    inquiry_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, inquiry_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company || null,
        phone: formData.phone || null,
        message: formData.message,
        inquiry_type: formData.inquiry_type || "General Inquiry"
      });

      if (response.data.status === "success") {
        setIsSubmitted(true);
        toast.success("Your inquiry has been submitted successfully!");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to submit inquiry. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-md text-center p-8">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="heading-secondary mb-4" data-testid="contact-success-headline">
            Thank You for Your Inquiry
          </h1>
          <p className="body-default mb-8" data-testid="contact-success-message">
            We have received your message and will respond within 1-2 business days. 
            For urgent matters, please email us directly at{" "}
            <a href="mailto:sales@clearpathrapid.com" className="text-sky-600 font-medium">
              sales@clearpathrapid.com
            </a>
          </p>
          <Button 
            onClick={() => setIsSubmitted(false)} 
            variant="outline"
            className="border-slate-300"
            data-testid="contact-submit-another"
          >
            Submit Another Inquiry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
          <div className="max-w-3xl">
            <span className="caption-text text-sky-600 mb-4 block">Contact Us</span>
            <h1 className="heading-primary mb-4" data-testid="contact-headline">
              Get in Touch
            </h1>
            <p className="body-large" data-testid="contact-description">
              Contact our sales team for pricing, product availability, and inquiries about our 
              point-of-care diagnostic tests.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="bg-slate-50 section-gap">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="heading-tertiary mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                {/* Primary Contact */}
                <div className="p-4 bg-white rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-1">Sales Team</h3>
                  <p className="text-sm text-slate-500 mb-3">Pricing & Product Inquiries</p>
                  <div className="space-y-2">
                    <a 
                      href="mailto:sales@clearpathrapid.com" 
                      className="flex items-center gap-2 text-sky-600 hover:text-sky-700 transition-colors text-sm"
                      data-testid="contact-sales-email"
                    >
                      <Mail className="w-4 h-4" />
                      sales@clearpathrapid.com
                    </a>
                    <a 
                      href="tel:817-508-6232" 
                      className="flex items-center gap-2 text-sky-600 hover:text-sky-700 transition-colors text-sm"
                      data-testid="contact-phone"
                    >
                      <Phone className="w-4 h-4" />
                      (817) 508-6232
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="feature-icon flex-shrink-0">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Business Hours</h3>
                    <p className="text-slate-600">Monday - Friday</p>
                    <p className="text-slate-600">9:00 AM - 5:00 PM CST</p>
                  </div>
                </div>
              </div>

              {/* Professional Use Notice */}
              <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Activity className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 text-sm mb-1">
                      B2B Sales Only
                    </p>
                    <p className="text-sm text-amber-700">
                      ClearPath serves healthcare facilities and professional buyers only. 
                      Products are not available for consumer purchase.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl p-8 border border-slate-200">
                <h2 className="heading-tertiary mb-6">Send Us a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                      <Label htmlFor="name" className="form-label">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="form-input"
                        data-testid="contact-name-input"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <Label htmlFor="email" className="form-label">
                        Email Address <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input"
                        data-testid="contact-email-input"
                      />
                    </div>

                    {/* Company */}
                    <div>
                      <Label htmlFor="company" className="form-label">
                        Company / Facility Name
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        placeholder="Healthcare facility name"
                        value={formData.company}
                        onChange={handleChange}
                        className="form-input"
                        data-testid="contact-company-input"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <Label htmlFor="phone" className="form-label">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  {/* Inquiry Type */}
                  <div>
                    <Label htmlFor="inquiry_type" className="form-label">
                      Inquiry Type
                    </Label>
                    <Select value={formData.inquiry_type} onValueChange={handleSelectChange}>
                      <SelectTrigger className="w-full" data-testid="contact-inquiry-type-select">
                        <SelectValue placeholder="Select inquiry type" />
                      </SelectTrigger>
                      <SelectContent>
                        {inquiryTypes.map((type) => (
                          <SelectItem key={type} value={type} data-testid={`inquiry-type-${type.toLowerCase().replace(/\s/g, '-')}`}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message" className="form-label">
                      Message <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Please describe your inquiry, including any specific products of interest and estimated quantities if applicable."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="form-textarea"
                      data-testid="contact-message-input"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full md:w-auto bg-sky-600 hover:bg-sky-700 text-white px-8 py-6 text-base"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit Inquiry"
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
